import { state as data_sources_state } from "@/views/customize/data-sources/data/state";
import {
  ApplicationType,
  DataSourceType,
  ScanSession,
} from "@/views/customize/data-sources/data/model";
import { UseDropdownItems } from "@/common/composition/dropdown-items";
import { UseLoadingAction } from "@/common/composition/loading";
import { UserSettings } from "@/common/api/user-settings.api";
import { Ref, computed, ref } from "@vue/composition-api";
import {
  get_finding_count,
  get_finding_entities,
} from "@/common/api/findings/findings.api";
import { SharedState } from "@/common/state";
import { SnowFlakeItem } from "./model";
import { UseCollection } from "@/common/composition/use-collection";

type CountItem = {
  id: string;
  name: string;
  count: number;
};

const SnowflakeLabelsSettings = UserSettings.key<string[]>(
  "snowflake",
  () => []
);

const [loading, set_loading] = UseLoadingAction<
  "init" | "change_session" | "change_labels"
>(false, "init");

const selected_labels: Ref<string[]> = ref([]);

const [
  sessions,
  selected_session,
  _set_selected_session,
  init_sessions,
] = UseDropdownItems<ScanSession>([]);

const row_items: Ref<Array<CountItem>> = ref([]);
const selected_row_item: Ref<null | CountItem> = ref(null);

const items = UseCollection<SnowFlakeItem, { cursor: string }>(
  // @ts-ignore
  async ({ cursor }) => {
    return await get_finding_entities("Snowflake")({
      application: "snowflake",
      service: "snowflake",
      axis_z: {
        type: "Session",
        value: selected_session.value!.id,
      },
      type: "sensitive_db_obj",
      cursor,
      label_ids: [selected_row_item.value!.id],
    });
  }
);

const set_selected_row_item = async (item: CountItem | null) => {
  if (item) {
    const label = SharedState.getInfoType(item.id);
    if (SharedState.restrictions.is_restricted_info(label.code)) {
      SharedState.restrictions.show_restrictions_modal("info", label.labelName);
      return;
    }
    selected_row_item.value = item;
    items.clear();
    await items.load_items({ cursor: "" });
  } else {
    selected_row_item.value = null;
    items.clear();
  }
};

const load_items = async () => {
  const res = await get_finding_count({
    application: "snowflake" as ApplicationType,
    service: "snowflake" as DataSourceType,
    axis_z: {
      type: "Session",
      value: selected_session.value!.id,
    },
    label_ids: selected_labels.value,
    type: "sensitive_db_obj",
  });
  if (res.success) {
    row_items.value = res.value
      .filter((f) => SharedState.allInfoTypes.value[f.labelId])
      .map((d) => {
        const label = SharedState.allInfoTypes.value[d.labelId];
        return {
          id: d.labelId,
          count: d.count,
          name: label.labelName,
        };
      });
  }
};

const set_selected_session = async (session: ScanSession) => {
  // @ts-ignore
  _set_selected_session(session);
  set_selected_row_item(null);
  if (selected_labels_count.value > 0) {
    set_loading(true, "change_session");
    await load_items();
    set_loading(false, "change_session");
  }
};

const selected_labels_count = computed(() => selected_labels.value.length);
const save_selected_labels_ids = async (ids: string[]) => {
  selected_labels.value = ids;
  set_loading(true, "change_labels");
  await load_items();
  await SnowflakeLabelsSettings.update(ids);
  set_loading(false, "change_labels");
};

const init = async (force = false) => {
  set_loading(true, "init");
  selected_labels.value = await SnowflakeLabelsSettings.get();
  selected_labels.value = selected_labels.value.filter(
    (id) => SharedState.allInfoTypes.value[id]
  );
  await data_sources_state.init();
  const res = await data_sources_state.ext.get_sessions_for_source("snowflake");
  if (res.success) {
    init_sessions(res.value);
    if (sessions.value.length > 0) {
      if (force) {
        set_selected_session(sessions.value[0]);
      } else {
        set_selected_session(selected_session.value || sessions.value[0]);
      }
    }
  }
  set_loading(false, "init");
};

export const state = {
  init,
  loading,
  row_items,
  sessions,
  selected_session,
  set_selected_session,
  selected_labels_count,
  save_selected_labels_ids,
  items,
  selected_row_item,
  set_selected_row_item,
  selected_labels,
};
